import { clone, delay } from "./client";
import { api, USE_MOCK_API } from "./http";
import { clearSession, hasSession, setSession, getRefreshToken } from "./session";
import { MOCK_USER } from "./mock-data";
import type { Role, User } from "./types";

const SESSION_KEY = "seen.session";

/** Raw session payload returned by bootstrap / login / register-verify. */
export interface AuthTokens {
  user: RemoteUser;
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}

interface RemoteUser {
  id: string;
  email: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  role?: Role;
  createdAt?: string;
}

export interface SignupResult {
  /** True when the backend sent a 6-digit OTP that must be verified. */
  otpRequired: boolean;
  email: string;
  user: User | null;
}

/** The backend speaks `fullName`; the UI splits it into first / last. */
export function normalizeUser(raw: RemoteUser): User {
  const full = (raw.fullName ?? `${raw.firstName ?? ""} ${raw.lastName ?? ""}`).trim();
  const [first, ...rest] = full.split(/\s+/).filter(Boolean);
  return {
    id: raw.id,
    email: raw.email,
    role: raw.role === "customer" ? "user" : raw.role,
    firstName: raw.firstName ?? first ?? "",
    lastName: raw.lastName ?? rest.join(" "),
    fullName: full,
    phone: raw.phone ?? null,
    createdAt: raw.createdAt,
  };
}

/* -------------------------------------------------------------------------- */
/*  Mock session (used while no backend is configured)                        */
/* -------------------------------------------------------------------------- */

function readSession(): User | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

function writeSession(user: User | null) {
  if (typeof window === "undefined") return;
  if (user) window.localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  else window.localStorage.removeItem(SESSION_KEY);
}

/* -------------------------------------------------------------------------- */
/*  First-run bootstrap                                                      */
/* -------------------------------------------------------------------------- */

/** GET /api/auth/status — show the "create owner account" screen while true. */
export async function getAuthStatus(): Promise<{ needsBootstrap: boolean }> {
  if (USE_MOCK_API) return delay({ needsBootstrap: false }, 80);
  try {
    return await api.get<{ needsBootstrap: boolean }>("/auth/status");
  } catch {
    return { needsBootstrap: false };
  }
}

/** POST /api/auth/bootstrap — creates the very first super_admin. */
export async function bootstrapOwner(input: {
  email: string;
  password: string;
  fullName: string;
}): Promise<User> {
  const result = await api.post<AuthTokens>("/auth/bootstrap", input);
  setSession(result);
  return normalizeUser(result.user);
}

/* -------------------------------------------------------------------------- */
/*  Sign in / sign up                                                         */
/* -------------------------------------------------------------------------- */

export async function login(input: { email: string; password: string }): Promise<User> {
  if (!input.email || !input.password) {
    throw new Error("Please enter your email and password.");
  }

  if (!USE_MOCK_API) {
    const result = await api.post<AuthTokens>("/auth/login", input);
    setSession(result);
    return normalizeUser(result.user);
  }

  const user: User = { ...clone(MOCK_USER), email: input.email };
  writeSession(user);
  return delay(user, 400);
}

export async function signup(input: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}): Promise<SignupResult> {
  if (!input.email || !input.password || !input.firstName) {
    throw new Error("Please complete every field to create your account.");
  }

  if (!USE_MOCK_API) {
    await api.post<{ otpSent: boolean; email: string }>("/auth/register", input);
    return { otpRequired: true, email: input.email, user: null };
  }

  const user: User = {
    id: "u-new",
    firstName: input.firstName,
    lastName: input.lastName,
    email: input.email,
  };
  writeSession(user);
  await delay(null, 400);
  return { otpRequired: false, email: input.email, user };
}

/** Second signup step — exchanges the emailed OTP for a token pair. */
export async function verifySignupOtp(input: { email: string; code: string }): Promise<User> {
  if (!input.code || input.code.trim().length < 4) {
    throw new Error("Enter the 6-digit code we emailed you.");
  }

  if (!USE_MOCK_API) {
    const result = await api.post<AuthTokens>("/auth/register/verify", {
      email: input.email,
      code: input.code.trim(),
    });
    setSession(result);
    return normalizeUser(result.user);
  }

  const user = readSession() ?? { ...clone(MOCK_USER), email: input.email };
  writeSession(user);
  return delay(user, 300);
}

/* -------------------------------------------------------------------------- */
/*  Password reset — OTP + reset-token flow                                   */
/* -------------------------------------------------------------------------- */

/** POST /api/auth/forgot-password */
export async function requestPasswordReset(email: string): Promise<{ sent: boolean }> {
  if (!email) throw new Error("Please enter the email tied to your account.");
  if (!USE_MOCK_API) {
    const result = await api.post<{ ok?: boolean; sent?: boolean }>("/auth/forgot-password", {
      email,
    });
    return { sent: result?.sent ?? true };
  }
  return delay({ sent: true }, 400);
}

/** POST /api/auth/forgot-password/verify-otp → single-use reset token (10 min). */
export async function verifyPasswordResetOtp(input: {
  email: string;
  code: string;
}): Promise<{ resetToken: string }> {
  if (!USE_MOCK_API) {
    const result = await api.post<{ reset_token?: string; resetToken?: string }>(
      "/auth/forgot-password/verify-otp",
      { email: input.email, code: input.code.trim() },
    );
    const token = result.reset_token ?? result.resetToken;
    if (!token) throw new Error("That code is no longer valid.");
    return { resetToken: token };
  }
  return delay({ resetToken: "mock-reset-token" }, 300);
}

/** POST /api/auth/reset-password — signs the account out everywhere. */
export async function resetPasswordWithToken(input: {
  token: string;
  password: string;
}): Promise<{ success: boolean }> {
  if (!USE_MOCK_API) {
    const result = await api.post<{ success?: boolean }>("/auth/reset-password", input);
    return { success: result?.success ?? true };
  }
  return delay({ success: true }, 400);
}

/**
 * Classic one-step reset (`/auth/password/reset`) kept for callers that hold the
 * emailed code and the new password at the same time.
 */
export async function resetPassword(input: {
  email: string;
  code: string;
  password: string;
}): Promise<{ success: boolean }> {
  if (!USE_MOCK_API) {
    const { resetToken } = await verifyPasswordResetOtp({ email: input.email, code: input.code });
    return resetPasswordWithToken({ token: resetToken, password: input.password });
  }
  return delay({ success: true }, 400);
}

/* -------------------------------------------------------------------------- */
/*  Session                                                                   */
/* -------------------------------------------------------------------------- */

export async function getCurrentUser(): Promise<User | null> {
  if (!USE_MOCK_API) {
    if (!hasSession()) return null;
    try {
      return normalizeUser(await api.get<RemoteUser>("/auth/me"));
    } catch {
      return null;
    }
  }
  return delay(readSession(), 120);
}

/** PATCH /api/auth/me */
export async function updateAccount(input: {
  fullName?: string;
  phone?: string;
}): Promise<User> {
  if (!USE_MOCK_API) return normalizeUser(await api.patch<RemoteUser>("/auth/me", input));
  const current = readSession();
  const next = { ...(current ?? clone(MOCK_USER)), fullName: input.fullName ?? current?.fullName };
  writeSession(next as User);
  return delay(next as User, 200);
}

export async function logout(): Promise<void> {
  if (!USE_MOCK_API) {
    const refreshToken = getRefreshToken();
    try {
      await api.post("/auth/logout", { refreshToken: refreshToken ?? "" });
    } catch {
      /* the local session is cleared regardless */
    }
    clearSession();
    return;
  }
  writeSession(null);
  return delay(undefined, 120);
}
